'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/browser';
import { useAuthContext } from '@/components/providers/AuthProvider';
import {
  Briefcase,
  Search,
  Grid,
  List,
  Plus,
  AlertTriangle,
  CheckCircle,
  Info,
  SlidersHorizontal,
} from 'lucide-react';
import JobCard from '@/components/JobCard';
import ClosedJobCard, { type ClosedJobCardJob } from '@/components/ClosedJobCard';
import { publicJobsQuery } from '@/lib/queries/jobs';
import PageHero from '@/components/layout/PageHero';
import { Card, Input, Select, Button as DsButton } from '@/components/ds';
import { EmptyState } from '@/components/EmptyState';
import { cn } from '@/lib/utils';

export default function JobsPage() {
  const searchParams = useSearchParams();
  const { user } = useAuthContext();
  const supabase = createClient();

  const [jobs, setJobs] = useState<any[]>([]);
  const [closedJobs, setClosedJobs] = useState<ClosedJobCardJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    jobType: 'all',
    location: 'all',
    paid: 'all',
  });
  useEffect(() => {
    loadJobs();
    const interval = setInterval(loadJobs, 30000);
    return () => clearInterval(interval);
  }, []);

  // For more reliable expiry in production, consider a Supabase cron job:
  // SELECT cron.schedule('expire-jobs', '0 * * * *', $$
  //   UPDATE job_postings
  //   SET is_archived = true
  //   WHERE status = 'published'
  //   AND is_archived = false
  //   AND post_removal_date IS NOT NULL
  //   AND post_removal_date < NOW();
  // $$);
  const checkExpiredJobs = async () => {
    try {
      const now = new Date().toISOString();
      const { data: expired, error: expireErr } = await supabase
        .from('job_postings')
        .update({ is_archived: true })
        .eq('status', 'published')
        .eq('is_archived', false)
        .not('post_removal_date', 'is', null)
        .lt('post_removal_date', now)
        .select('id, title');

      if (expireErr) {
        console.warn('[Jobs] Expiry check failed:', expireErr.message);
        return;
      }
      if (expired?.length) {
        console.log('[Jobs] Archived expired jobs:', expired.map((j: any) => j.title));
      }
    } catch (err) {
      console.warn('[Jobs] Expiry check error:', err);
    }
  };

  // Always fetch published jobs directly from Supabase — no admin API (401 must not affect this page)
  const loadJobs = async () => {
    try {
      setLoading(true);
      // Fire-and-forget the expiry sweep — do NOT block the render on a write.
      // The active-list client filter below (post_removal_date > now) already
      // hides past-date jobs immediately, so display is correct even before the
      // sweep runs; the sweep just moves expired jobs into the Closed section on
      // a subsequent load/poll. FOLLOW-UP: relocate this to the vercel cron
      // infra (src/app/api/cron/*) or the pg_cron noted above, and delete the
      // client call entirely so no user's page-load triggers the UPDATE.
      void checkExpiredJobs();

      console.log('[Jobs] Loading published, non-archived jobs only');
      // "Closed" definition for the public board:
      //   status = 'published' AND (is_archived = true OR is_filled = true)
      // - Naturally expired postings get is_archived=true via checkExpiredJobs
      //   above (their status stays 'published').
      // - Posters' "Close early" sets is_filled=true.
      // - Admin-rejected jobs flip status to 'rejected', so they're excluded.
      // - pending_review jobs are excluded because status filter requires
      //   'published'.
      // Heuristic note: there's no dedicated closed_at timestamp on the
      // table; updated_at is the best proxy for "when did it close" and
      // is what we sort the closed section by. If we ever add closed_at,
      // swap the sort and remove this note.
      const [activeRes, closedRes] = await Promise.all([
        publicJobsQuery(supabase).order('created_at', { ascending: false }).limit(50),
        supabase
          .from('job_postings')
          .select('id, title, tags, posted_by, status, is_archived, is_filled, updated_at, post_removal_date')
          .eq('status', 'published')
          .or('is_archived.eq.true,is_filled.eq.true')
          .order('updated_at', { ascending: false })
          .limit(50),
      ]);
      const { data, error: queryErr } = activeRes;

      if (queryErr) {
        console.error('[Jobs] Query error:', JSON.stringify(queryErr));
        setJobs([]);
        setClosedJobs([]);
        setError(`Failed to load jobs: ${queryErr.message}`);
        return;
      }
      setError(null);
      console.log(`[Jobs] Loaded ${data?.length ?? 0} active jobs, ${closedRes.data?.length ?? 0} closed`);

      // Client-side expiry filter — only remove jobs where removal date is set AND in the past
      const now = new Date();
      const visible = (data || []).filter((job: any) => {
        if (!job.post_removal_date) return true;
        return new Date(job.post_removal_date) > now;
      });

      const closedRaw = (closedRes.data || []) as any[];

      // Stitch poster profiles for both sets in a single .in() lookup —
      // no FK joins, one round-trip per profile column we need.
      const allPosterIds = Array.from(
        new Set(
          [...visible, ...closedRaw]
            .map((j) => j.posted_by ?? j.poster_id)
            .filter(Boolean) as string[]
        )
      );
      const postersById: Record<string, any> = {};
      if (allPosterIds.length > 0) {
        const { data: posters } = await supabase
          .from('profiles')
          .select('id, display_name, username, avatar_url')
          .in('id', allPosterIds);
        (posters || []).forEach((p: any) => {
          postersById[p.id] = p;
        });
      }

      const activeWithPosters = visible.map((job: any) => ({
        ...job,
        poster: postersById[job.posted_by ?? job.poster_id] || null,
      }));
      setJobs(activeWithPosters);

      // Sort closed by best available timestamp. Prefer post_removal_date
      // when set (the natural closing date); fall back to updated_at.
      const closedSorted = closedRaw
        .map((job) => {
          const closedAt =
            (job.post_removal_date as string | null) ||
            (job.updated_at as string | null) ||
            new Date().toISOString();
          return {
            id: job.id as string,
            title: (job.title as string) || 'Untitled role',
            tags: (job.tags as string[] | null) || null,
            closed_at: closedAt,
            poster: postersById[job.posted_by ?? job.poster_id] || null,
          } satisfies ClosedJobCardJob;
        })
        .sort((a, b) => (a.closed_at < b.closed_at ? 1 : -1))
        .slice(0, 50);
      setClosedJobs(closedSorted);
    } catch (err) {
      console.error('[Jobs] Unexpected error:', err);
      setJobs([]);
      setClosedJobs([]);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch =
      job.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = filters.jobType === 'all' || job.job_type === filters.jobType;
    const matchesLocation = filters.location === 'all' || job.location?.includes(filters.location);
    const matchesPaid =
      filters.paid === 'all' ||
      (filters.paid === 'paid' && job.rate) ||
      (filters.paid === 'unpaid' && !job.rate);

    return matchesSearch && matchesType && matchesLocation && matchesPaid;
  });

  // Search/filter UI applies to active jobs only. When any filter is
  // active, hide the closed section so the page doesn't show closed
  // cards that don't match the user's query.
  const filtersActive =
    searchQuery.trim().length > 0 ||
    filters.jobType !== 'all' ||
    filters.location !== 'all' ||
    filters.paid !== 'all';
  const showClosedSection = !filtersActive && closedJobs.length > 0;

  return (
    <div className="min-h-screen bg-surface-0 pb-16">
      <PageHero
        title="Jobs & Casting"
        subtitle="Discover curated opportunities from verified posters"
        variant="strip"
        background="gradient-blue-purple"
        primaryAction={
          user
            ? {
                label: 'Post a Job',
                href: '/jobs/new',
                icon: <Plus className="h-4 w-4" />,
              }
            : undefined
        }
      />

      <div className="max-w-7xl mx-auto px-4">
        {searchParams.get('submitted') === 'true' && (
          <Card className="bg-accent-soft border-accent mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" strokeWidth={1.75} />
              <p className="text-text-primary text-sm">
                Your job has been submitted for review. An admin will approve it shortly.
              </p>
            </div>
          </Card>
        )}
        {searchParams.get('removed') === 'true' && (
          <Card className="mb-4">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-text-secondary flex-shrink-0" strokeWidth={1.75} />
              <p className="text-text-primary text-sm">
                The job has been removed from the listing.
              </p>
            </div>
          </Card>
        )}

        {/* Search & Filters — collapsed by default. The dropdown row reveals
            on toggle so users see job cards above the fold immediately. */}
        <Card className="mb-4 p-3 sm:p-4">
          {/* Top row: search + filters toggle + view mode */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary pointer-events-none"
                strokeWidth={1.75}
              />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search jobs..."
                className="pl-9"
              />
            </div>
            <button
              type="button"
              onClick={() => setFiltersExpanded((v) => !v)}
              aria-expanded={filtersExpanded}
              aria-controls="jobs-filter-row"
              className={cn(
                'inline-flex h-8 items-center gap-1.5 px-2.5 rounded-sm border text-xs font-medium transition-colors flex-shrink-0',
                filtersExpanded
                  ? 'bg-accent-soft border-accent text-accent'
                  : 'bg-surface-2 border-border-subtle text-text-secondary hover:text-text-primary hover:border-border-default'
              )}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" strokeWidth={1.75} />
              <span className="hidden sm:inline">Filters</span>
            </button>
            <div className="flex gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                aria-label="List view"
                aria-pressed={viewMode === 'list'}
                className={cn(
                  'w-8 h-8 rounded-sm transition-colors flex items-center justify-center',
                  viewMode === 'list'
                    ? 'bg-accent text-text-on-accent'
                    : 'bg-surface-2 border border-border-subtle text-text-secondary hover:text-text-primary'
                )}
              >
                <List className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
                aria-pressed={viewMode === 'grid'}
                className={cn(
                  'w-8 h-8 rounded-sm transition-colors flex items-center justify-center',
                  viewMode === 'grid'
                    ? 'bg-accent text-text-on-accent'
                    : 'bg-surface-2 border border-border-subtle text-text-secondary hover:text-text-primary'
                )}
              >
                <Grid className="w-3.5 h-3.5" strokeWidth={1.75} />
              </button>
            </div>
          </div>

          {filtersExpanded && (
            <div
              id="jobs-filter-row"
              className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2"
            >
              <Select
                value={filters.jobType}
                onChange={(e) => setFilters({ ...filters, jobType: e.target.value })}
              >
                <option value="all">All Job Types</option>
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="one_time">One Time</option>
              </Select>
              <Select
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              >
                <option value="all">All Locations</option>
                <option value="Remote">Remote</option>
                <option value="Los Angeles">Los Angeles</option>
                <option value="New York">New York</option>
                <option value="Chicago">Chicago</option>
              </Select>
              <Select
                value={filters.paid}
                onChange={(e) => setFilters({ ...filters, paid: e.target.value })}
              >
                <option value="all">All Compensation</option>
                <option value="paid">Paid Only</option>
                <option value="unpaid">Unpaid/Volunteer</option>
              </Select>
            </div>
          )}
        </Card>

        {/* Error Banner */}
        {error && (
          <Card className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-status-busy flex-shrink-0" strokeWidth={1.75} />
              <p className="text-status-busy text-sm font-medium">Failed to load jobs</p>
            </div>
            <p className="text-text-secondary text-xs">{error}</p>
            <button
              onClick={() => {
                setError(null);
                void loadJobs();
              }}
              className="mt-2 text-xs text-accent hover:text-accent-hover underline"
            >
              Try again
            </button>
          </Card>
        )}

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-text-secondary text-xs">
            <span className="font-semibold text-text-primary">{filteredJobs.length}</span>{' '}
            opportunities found
          </p>
        </div>

        {/* Jobs list — three real cases plus the filtered-empty fallback:
              A. ≥1 active job → grid + (closed section as sibling)
              B. 0 active, no filters, ≥1 closed → compact notice +
                 closed section above the fold; small secondary
                 links below for community/tags discovery.
              C. 0 active, no filters, 0 closed → full empty state
                 (brand-new-platform messaging, unchanged).
              D. 0 active, filters applied → "No matching jobs" with
                 Clear filters (unchanged). Closed section stays
                 hidden because showClosedSection requires no filters. */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4" />
              <p className="text-text-secondary text-sm">Loading jobs...</p>
            </div>
          </div>
        ) : filteredJobs.length > 0 ? (
          // Case A
          <div
            className={
              viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : 'space-y-4'
            }
          >
            {filteredJobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        ) : filtersActive ? (
          // Case D — filters present + no matches.
          <EmptyState
            size="lg"
            icon={Briefcase}
            title="No matching jobs"
            description="No jobs match your current filters. Clear them to see everything that's open right now."
            action={
              <DsButton
                variant="primary"
                size="md"
                onClick={() => {
                  setSearchQuery('');
                  setFilters({ jobType: 'all', location: 'all', paid: 'all' });
                }}
              >
                Clear filters
              </DsButton>
            }
          />
        ) : closedJobs.length > 0 ? (
          // Case B — no active openings, but we have closed jobs to
          // show as social proof. Compact one-line notice in place of
          // the full empty state; the "Recently closed" section
          // renders directly below (as a sibling) so closed cards
          // appear above the fold. Browse community/tags links are
          // demoted to small text links below the closed cards.
          <p className="text-sm text-text-secondary">
            No open opportunities right now — here&apos;s what&apos;s wrapped recently:
          </p>
        ) : (
          // Case C — truly empty platform.
          <EmptyState
            size="lg"
            icon={Briefcase}
            title="No active jobs right now"
            description="Casting calls and auditions get posted here as they come in. Check back soon, or follow tags you're interested in to be notified."
            action={
              <DsButton variant="secondary" size="md" onClick={() => (window.location.href = '/home')}>
                Browse community
              </DsButton>
            }
            secondaryAction={
              <DsButton variant="ghost" size="md" onClick={() => (window.location.href = '/tags')}>
                Browse tags
              </DsButton>
            }
          />
        )}

        {/* Recently closed — social proof. Hidden during search/filter
            so closed cards never appear in or alongside filtered
            results, and hidden entirely when there are no closed jobs
            (no orphan header). Spacing tightens in Case B because the
            compact notice above it is much smaller than an active
            grid or empty state. */}
        {!loading && showClosedSection && (
          <section className={filteredJobs.length > 0 ? 'mt-10 sm:mt-12' : 'mt-4 sm:mt-5'}>
            <div className="flex items-baseline justify-between gap-3 mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-text-secondary tracking-tight">
                Recently closed
              </h2>
              <p className="text-xs text-text-tertiary tabular-nums">
                {closedJobs.length}
              </p>
            </div>
            <div
              className={
                viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
                  : 'space-y-3'
              }
            >
              {closedJobs.map((job) => (
                <ClosedJobCard key={job.id} job={job} />
              ))}
            </div>

            {/* Case B secondary nav — only when this section replaced
                the full empty state. Small text links, not buttons. */}
            {filteredJobs.length === 0 && !filtersActive && (
              <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-tertiary">
                <span>Looking for something else?</span>
                <Link href="/home" className="text-accent hover:text-accent-hover">
                  Browse community
                </Link>
                <Link href="/tags" className="text-accent hover:text-accent-hover">
                  Browse tags
                </Link>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
